import { openDB, IDBPDatabase } from 'idb'

type ProductList = any[]

type CacheEntry = {
  key: string
  data: string // possibly compressed JSON
  size: number // bytes (approx)
  createdAt: number
  lastAccessed: number
  ttl?: number // ms
}

const DB_NAME = 'products-cache-db'
const DB_VERSION = 1
const STORE = 'pages'

let dbPromise: Promise<IDBPDatabase> | null = null

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'key' })
        }
      },
    })
  }
  return dbPromise
}

async function maybeCompress(text: string) {
  try {
    // try dynamic import of lz-string if available
    // fall back to raw string if not
     
    const mod = await import('lz-string')
    return { compressed: true, payload: mod.compressToUTF16(text) }
  } catch (e) {
    return { compressed: false, payload: text }
  }
}

async function maybeDecompress(entry: CacheEntry) {
  try {
    const mod = await import('lz-string')
    // if compressed, assume entry.data is compressed
    try {
      const decompressed = mod.decompressFromUTF16(entry.data)
      if (decompressed == null) return null
      return decompressed
    } catch (e) {
      // not compressed or decompression failed
      return entry.data
    }
  } catch (e) {
    return entry.data
  }
}

function approxBytesOfString(str: string) {
  // rough UTF-16 bytes
  return str.length * 2
}

export async function generateCacheKey(params: Record<string, any>) {
  // deterministic key: sort keys
  const sorted: Record<string, any> = {}
  Object.keys(params)
    .sort()
    .forEach((k) => {
      sorted[k] = params[k]
    })
  return JSON.stringify(sorted)
}

export async function getCachedProducts(key: string, maxAgeMs = 1000 * 60 * 60 * 24) {
  const db = await getDB()
  const entry = (await db.get(STORE, key)) as CacheEntry | undefined
  if (!entry) return null
  const now = Date.now()
  if (entry.ttl && now - entry.createdAt > entry.ttl) {
    // expired
    await db.delete(STORE, key)
    return null
  }
  if (now - entry.createdAt > maxAgeMs) {
    // expired by external max age
    await db.delete(STORE, key)
    return null
  }
  // touch lastAccessed
  entry.lastAccessed = now
  await db.put(STORE, entry)
  const json = await maybeDecompress(entry)
  try {
    return JSON.parse(json as string) as ProductList
  } catch (e) {
    return null
  }
}

export async function setCachedProducts(key: string, products: ProductList, options?: { ttl?: number; maxEntries?: number; maxBytes?: number }) {
  const db = await getDB()
  const now = Date.now()
  const raw = JSON.stringify(products)
  const { compressed, payload } = await maybeCompress(raw)
  const size = approxBytesOfString(payload)
  const entry: CacheEntry = {
    key,
    data: payload,
    size,
    createdAt: now,
    lastAccessed: now,
    ttl: options?.ttl,
  }
  await db.put(STORE, entry)
  await pruneCache({ maxEntries: options?.maxEntries ?? 200, maxBytes: options?.maxBytes ?? 5_000_000 })
}

export async function pruneCache({ maxEntries = 200, maxBytes = 5_000_000 } = {}) {
  const db = await getDB()
  const all = (await db.getAll(STORE)) as CacheEntry[]
  if (all.length === 0) return
  // compute totals
  let totalBytes = all.reduce((s, e) => s + (e.size || 0), 0)
  if (all.length <= maxEntries && totalBytes <= maxBytes) return
  // sort by lastAccessed asc (oldest first)
  const sorted = all.sort((a, b) => a.lastAccessed - b.lastAccessed)
  for (const entry of sorted) {
    if (all.length <= maxEntries && totalBytes <= maxBytes) break
    await db.delete(STORE, entry.key)
    totalBytes -= entry.size || 0
    // remove from all (not strictly necessary)
    all.shift()
  }
}

export async function clearCache() {
  const db = await getDB()
  await db.clear(STORE)
}

export async function cacheStats() {
  const db = await getDB()
  const all = (await db.getAll(STORE)) as CacheEntry[]
  return {
    count: all.length,
    totalBytes: all.reduce((s, e) => s + (e.size || 0), 0),
    oldestAccess: all.reduce((min, e) => Math.min(min, e.lastAccessed), Date.now()),
  }
}

export default {
  generateCacheKey,
  getCachedProducts,
  setCachedProducts,
  pruneCache,
  clearCache,
  cacheStats,
}
